<!-- Responsible for viewing/editing the currently-loaded game -->

<script setup>
import { computed, reactive, onMounted, ref, watch } from 'vue';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { v4 as uuid } from 'uuid';
import { AnFilledStar, AnOutlinedStar } from "@kalimahapps/vue-icons";

import { FormatStyles } from '@/util/FormatStyles.js';

import moveAndDelete from '@/components/MoveAndDelete.vue';
// import memorySquares from './components/memorySquares';

import { useTriviaStore } from '@/stores/trivia.js';
const store = useTriviaStore();

const editor = ClassicEditor;
const editorConfig = ref({});

const props = defineProps({
  hideAnswers: Boolean,
  workingQuestion: Object,
  collection: String,
  manager: Boolean, // True for accessing through Data Manager, False for accessing through Question Editor
});

const activeRound = computed ({
  get() {
    return store.CurrentGame.activeRound;
  },
  set(newVal) {
    store.CurrentGame.activeRound = newVal;
  }
});

const activeQuestion = computed ({
  get() {
    return store.CurrentGame.activeQuestion;
  },
  set(newVal) {
    store.CurrentGame.activeQuestion = newVal;
  }
});

const emit = defineEmits(['close', 'update'])

/*
tools => display scores, answers, or question editor
generators => general reference for app use

questions is an array
each item in the array is an object (and represents one round of gameplay)
each 'round' object is number-keyed, and each key is a question
each question is an object that looks like this
{ cat: string     category of question to display
  timeAllotted: number
  q: array, containing question segments
  a: object with text, highlight (number tells the app which multiple choice answer to highlight)
  hint object (for special question types, like wordLeap)
}

The question's q array is filled with object segments,
where the type of question segment (ie text/image) is set,
as well as all the info needed to display that segment. 
*/

const getInitialTools = () => ({
  scores: false,
  answers: false,
  questions: false
});

const getInitialGenerators = () => ({
  wordLeap: false,
  sizeMatters: false,
  boxOffice: false,
  bank:[],
  seed: null,
  highlightedWords: [],
  promptInput: null,
  manager: false,
  showRound: null,
  showQuestion: null,
});


// Types of content segments. Questions are made up of one or more segments.
const segmentTypes = ref([
  { text: 'Text', value: 'text' },
  { text: 'Word Leap', value: 'wordLeap' },
  { text: 'Fill in the Blank', value: 'blankFill' },
  { text: 'Word Hunt', value: 'wordHunt' },
  { text: 'Synonym Scramble', value: 'synonymScramble' },
  { text: 'Word Frag', value: 'wordFrag' },
  { text: 'Secret Word', value: 'secretWord' },
  { text: 'Anagram Scramble', value: 'anagramScramble' },
  { text: 'AlphaBet Crush', value: 'alphabetCrush' },
  { text: 'Category Crush', value: 'categoryCrush' },
  { text: 'Word Cards', value: 'wordCards' },
  { text: 'Video', value: 'video' },
  { text: 'Audio', value: 'audio' },
  { text: 'Image', value: 'image' },
  { text: 'Clue & Answer', value: 'clueAnswer' },
  { text: 'Crossword', value: 'crossword' },
  { text: 'Bet', value: 'bet' },
]);

function changeSegment(type, key) {
  // Prepare segment data for newly selected segment type
  const q = workingQuestion.value.segments[key];
  if (q.showOnAnswer) {
    delete q.showOnAnswer;
  }
  if (q.dataAnswers) {
    delete q.dataAnswers;
  }
  if (q.pool) {
    delete q.pool;
  }
  if (q.fragCount) {
    delete q.fragCount;
  }
  if (q.categoryCount) {
    delete q.categoryCount;
  }
  if (q.randomLetter) {
    delete q.randomLetter;
  }
  switch (type) {
    default: {
      workingQuestion.value.segments[key].data = '';
      break;
    }
    case ('image'): {
      workingQuestion.value.segments[key].data = '';
      workingQuestion.value.segments[key].magnify = 1;
      workingQuestion.value.segments[key].showOnAnswer = false;
      break;
    }
    case ('video'):
    case ('text'):
    case ('audio'): {
      workingQuestion.value.segments[key].data = '';
      workingQuestion.value.segments[key].magnify = 1;
      break;
    }
    case ('multipleChoice'):
    {
      workingQuestion.value.segments[key].data = [];
      workingQuestion.value.segments[key].dataAnswers = [];
      workingQuestion.value.segments[key].magnify = 1;
      break
    }
    case ('clueAnswer'):
    {
      workingQuestion.value.segments[key].data = [];
      workingQuestion.value.segments[key].dataAnswers = [];
      workingQuestion.value.segments[key].magnify = 1;
      workingQuestion.value.segments[key].indexSplit = [1,2];
      workingQuestion.value.segments[key].hideIndex = false;
      workingQuestion.value.segments[key].forceAnswer = false;
      workingQuestion.value.segments[key].reverseIndex = false;
      workingQuestion.value.segments[key].columns = 1;
      break
    }
    case ('blankFill'):
    {
      // workingQuestion.value[categoryField.value] = 'Fill in the Blank';
      workingQuestion.value.segments[key].data = [];
      workingQuestion.value.segments[key].dataAnswers = [];
      workingQuestion.value.segments[key].magnify = 1;
      break
    }
    case ('wordLeap'):
      {
        // workingQuestion.value[categoryField.value] = 'Linguistic Leaps';
        workingQuestion.value.segments[key].data = [];
        workingQuestion.value.segments[key].dataAnswers = [];
        workingQuestion.value.segments[key].magnify = 1;
      break
    }
    case ('crossword'):
    {
      // workingQuestion.value[categoryField.value] = 'Crossword Challenge';
      workingQuestion.value.segments[key].data = [];
      workingQuestion.value.segments[key].dataAnswers = [];
      break
    }
    case ('bet'): {
      workingQuestion.value.segments[key].data = {
        time: 15,
        amount: 2,
      }
      break;
    }
    case('wordHunt') : {
      // workingQuestion.value[categoryField.value] = 'Word Hunt';
      workingQuestion.value.segments[key].minLength = 3;
      workingQuestion.value.segments[key].gridCount = 4;
      workingQuestion.value.segments[key].data = generateGridLetters(workingQuestion.value.segments[key].gridCount);
      break;
    }
    case('wordFrag') : {
      // workingQuestion.value[categoryField.value] = 'Word Frag';
      workingQuestion.value.segments[key].fragCount = 3;
      workingQuestion.value.segments[key].delayEach = true;
      workingQuestion.value.segments[key].data = generateFrags(workingQuestion.value.segments[key].fragCount);
      // TODO - These should load in from the store, not be attached to the question
      workingQuestion.value.segments[key].pool = fragPool;
      workingQuestion.value.segments[key].enableBonus = 1;
      break;
    }
    case('synonymScramble') : {
      // workingQuestion.value[categoryField.value] = 'Word Frag';
      workingQuestion.value.segments[key].wordCount = 3;
      workingQuestion.value.segments[key].delayEach = false;
      workingQuestion.value.segments[key].data = ['Happy', 'Funny', 'Easy'];
      // TODO - These should load in from the store, not be attached to the question
      // workingQuestion.value.segments[key].pool = fragPool;
      workingQuestion.value.segments[key].enableBonus = 1;
      break;
    }
    case('secretWord') : {
      // workingQuestion.value[categoryField.value] = 'What is it?';
      workingQuestion.value.segments[key].wordCount = 3;
      workingQuestion.value.segments[key].delayEach = true;
      workingQuestion.value.segments[key].data = generateSecretWords(workingQuestion.value.segments[key].wordCount);
      // TODO - These should load in from the store, not be attached to the question
      workingQuestion.value.segments[key].pool = secretWordPool;
      workingQuestion.value.segments[key].enableBonus = 1;
      break;
    }
    case('anagramScramble') : {
      // workingQuestion.value[categoryField.value] = 'Anagram Scramble';
      workingQuestion.value.segments[key].minLength = 3;
      workingQuestion.value.segments[key].gridLength = 2;
      workingQuestion.value.segments[key].gridHeight = 5;
      workingQuestion.value.segments[key].data = generateGridLetters(workingQuestion.value.segments[key].gridLength, workingQuestion.value.segments[key].gridHeight);
      break;
    }
    case('categoryCrush') : {
      // workingQuestion.value[categoryField.value] = 'Category Crush';
      workingQuestion.value.segments[key].randomLetter = 'A';
      workingQuestion.value.segments[key].categoryCount = 15;
      workingQuestion.value.segments[key].delayEach = false;
      workingQuestion.value.segments[key].data = generateCategories(workingQuestion.value.segments[key].categoryCount);
      // TODO - These should load in from the store, not be attached to the question
      workingQuestion.value.segments[key].pool = categoriesPool;
      workingQuestion.value.segments[key].enableBonus = 1;
      break;
    }
    case('wordCards'): {
      workingQuestion.value.segments[key].data = [];
      workingQuestion.value.segments[key].prompt = '';
      break;
    }
    case('alphabetCrush') : {
      // workingQuestion.value[categoryField.value] = 'ABC (AlphaBet Crush)';
      workingQuestion.value.segments[key].categoryCount = 1;
      workingQuestion.value.segments[key].randomLetter = 'A';
      workingQuestion.value.segments[key].delayEach = false;
      workingQuestion.value.segments[key].data = generateCategories(workingQuestion.value.segments[key].categoryCount);
      // TODO - These should load in from the store, not be attached to the question
      workingQuestion.value.segments[key].pool = categoriesPool;
      workingQuestion.value.segments[key].enableBonus = 1;
      break;
    }
  }
}

const themes = ['Fruits',
    'Celebrities',
    'Countries',
    'Animals',
    'Things in the Sky',
    'School Supplies',
    'Hobbies',
    'TV Shows',
    'Breakfast Foods',
    'Languages',
    'Famous Landmarks',
    'Fictional Characters',
    'Vegetables',
    'Things That Are Round',
    'Musical Instruments',
    'Insects',
    'Body Parts',
    'Cities',
    'Sports',
    'Books',
    'Clothing Brands',
    'Desserts',
    'Movies',
    'Emotions',
    'Ocean Creatures',
    'Board Games',
    'Famous Authors',
    'Things That Are Yellow',
    'Jobs',
    'Historical Figures',
    'Cartoon Characters',
    'Types of Transportation',
    'Pizza Toppings',
    'Gadgets',
    'Ice Cream Flavors',
    'Colors',
    'Mountains',
    'Restaurants',
    'Currencies',
    'Famous Paintings',
    'Video Games',
    'Spices',
    'Flowers',
    'Body Gestures',
    'Countries in Europe',
    'Mythological Creatures',
    'Condiments',
    'Types of Trees',
    'Planets',
    'Superheroes',
    'Food',
    'Nature',
    'Animals',
    'Plants',
    'Modes of Transportation',
    'Products',
    'Celebrities',
    'Places',
    'The Wild West',
    'Kings and Castles',
    'Wizards and Dragons',
    'Sci-fi',
    'Colors',
    'Fruits',
    'Vegetables',
    'Sports',
    'Body Parts',
    'Movies',
    'Horror Movies',
    'Music',
    'Outer Space',
    'Musical Instruments',
    'Historical Figures',
    'Occupations',
    'Mythological Creatures',
    'Mythology',
    'Desserts',
    'Weather',
    'Holidays',
    'Landmarks',
    'Cartoons',
    'Sea Creatures',
    'Technology',
    'Dogs',
  ];

function wordFragRules(bonusCount) {
  const themeKey = Math.floor(Math.random() * themes.length);
  const bonuses = [
    'The team(s) with the longest word',
    'The team(s) with the most words over 6 letters',
    'The team(s) with the most words over 7 letters',
    'The team(s) with the most words over 8 letters',
    'The team(s) with the most words over 9 letters',
    'Any team(s) with a word that has 10 or more letters',
    'Any team(s) with a word that has 11 or more letters',
    'Any team(s) with a word that has 12 or more letters',
    'The team(s) with the most unique words',
    'The team(s) with the most words that begin with the fragment',
    'The team(s) with the most words that end with the fragment',
    'The team(s) with the most words that do NOT begin with the fragment',
    'The team(s) with the most words that do NOT end with the fragment',
    'The team(s) with the most words that do NOT start or end with the fragment',
    `The team(s) with the most words fitting bonus theme '${themes[themeKey].toLowerCase()}'`,
  ];
  
  const rules = [];
  const bonusKeys = [];
  for (let a = 0; a < bonusCount; a += 1) {
    let bonusKey = Math.floor(Math.random() * bonuses.length);
    while (bonusKeys.indexOf(bonusKey) > -1) {
      bonusKey = Math.floor(Math.random() * bonuses.length);
    }
    bonusKeys.push(bonusKey);
    rules.push(`<strong>Bonus 2:</strong> ${bonuses[bonusKey]}`);
  }
  return rules;
};

const categoryCrushBonuses = [
    'The team(s) with the longest word',
    'The team(s) with the most words over 7 letters',
    'The team(s) with the most words over 8 letters',
    'The team(s) with the most words over 9 letters',
    'Any team(s) with a word that has 10 or more letters',
    'Any team(s) with a word that has 11 or more letters',
    'Any team(s) with a word that has 12 or more letters',
]

function categoryCrushRules(bonusCount) {
  const bonuses = categoryCrushBonuses;
  const rules = [];
  const bonusKeys = [];
  for (let a = 0; a < bonusCount; a += 1) {
    let bonusKey = Math.floor(Math.random() * bonuses.length);
    while (bonusKeys.indexOf(bonusKey) > -1) {
      bonusKey = Math.floor(Math.random() * bonuses.length);
    }
    bonusKeys.push(bonusKey);
    rules.push(`<strong>Bonus 2:</strong> ${bonuses[bonusKey]}`);
  }
  return rules;
};


const tools = ref(getInitialTools());
const generators = ref(getInitialGenerators());




const workingQuestion = computed(() => {
  if (props.manager) {
    return store.questions.filter(f => f._id === props.workingQuestion._id)[0];
  }
  return store.CurrentGame.rounds[activeRound.value][activeQuestion.value];
});

// const categoryField = computed(() => {
//   const categoryId = Object.entries(store.schema[props.collection]).filter(f => f[1].label === 'Category')[0][0];
//   return categoryId;
// });

// const timeField = computed(() => {
//   const timeId = Object.entries(store.schema[props.collection]).filter(f => f[1].label === 'Time Allotted')[0][0];
//   return timeId;
// });

// const segments = computed(() => {
//   const qSegmentId = Object.entries(store.schema[props.collection]).filter(f => f[1].label === 'Question Data')[0][0];
//   return qSegmentId;
// });

// const answerData = computed(() => {
//   const answerId = Object.entries(store.schema[props.collection]).filter(f => f[1].label === 'Answer Data')[0][0];
//   return answerId;
// });



async function generateWords(word) {
  let prompt;
  const gen = generators.value;
  if (!gen.promptInput) {
    if (word) {
      generators.value.highlightedWords.push(word);
    }
    if (gen.seed) {
      generators.value.highlightedWords.push(gen.seed);
    }
    if (gen.seed || word) {
      prompt = `Please generate 10 common words that are likely to follow ${word || gen.seed}`;
      if (!word && gen.seed) {
        generators.value.bank.push([gen.seed]);
        generators.value.seed = null;
      }
    } else {
      prompt = 'Please generate 10 common words between 3 and 9 characters.';
    }
  } else {
    prompt = gen.promptInput;
  }
  console.log(prompt);
  // model: 'text-davinci-003',
  const response = await openai.createCompletion({
    model: 'text-davinci-003',
    prompt,
    temperature: 0.6,
    max_tokens: 2500,
  });
  const words = removeFormatting(response.data.choices[0].text).split(' ').filter(b => Boolean(b));
  console.log(words);
  generators.value.bank.push(words);
}




function removeFormatting(word) {
  // Remove leading numbers from openai api returns
  const array = [];
  word.split('\n').filter(i => Boolean(i)).forEach((wrd) => {
    let toReturn = wrd.split('.');
    toReturn = toReturn.slice(1);
    array.push(toReturn);
  });
  console.log(array.join(' '));
  return array.join(' ');
}


function resetTools() {
  tools.value = getInitialTools();
}
function resetGenerators() {
  generators.value = getInitialGenerators();
}

const blankQuestion = () => ({
  cat: '',
  timeAllotted: 75,
  q: [],
  a: {
    text: '',
    highlight: [],
    reorder: [],
    hint: { show: false, amount: null, delay: false } },
});

const blankSegment = () => ({
  _id: uuid(),
  type: 'text',
  data: '',
  link: null,
  magnify: 1,
  meta: {
    display: false,
    displaySubsegment: -1,
    columns: 1,
  },
});

function addQuestionSegment(copy) {
  if (!copy) {
    workingQuestion.value.segments.push(blankSegment());
  } else {
    const qData = JSON.stringify(store.CurrentGame.rounds[activeRound.value - 1][activeQuestion.value].segments);
    store.CurrentGame.rounds[activeRound.value][activeQuestion.value].segments = JSON.parse(qData);
    const aData = JSON.stringify(store.CurrentGame.rounds[activeRound.value - 1][activeQuestion.value].answerData);
    store.CurrentGame.rounds[activeRound.value][activeQuestion.value].magnify = store.CurrentGame.rounds[activeRound.value - 1][activeQuestion.value].magnify;
    store.CurrentGame.rounds[activeRound.value][activeQuestion.value].points = store.CurrentGame.rounds[activeRound.value - 1][activeQuestion.value].points;
    store.CurrentGame.rounds[activeRound.value][activeQuestion.value].answerData = JSON.parse(aData);
    store.CurrentGame.rounds[activeRound.value][activeQuestion.value].category = store.CurrentGame.rounds[activeRound.value - 1][activeQuestion.value].category;
    store.CurrentGame.rounds[activeRound.value][activeQuestion.value].categorySubText = store.CurrentGame.rounds[activeRound.value - 1][activeQuestion.value].categorySubText;
    store.CurrentGame.rounds[activeRound.value][activeQuestion.value].type = store.CurrentGame.rounds[activeRound.value - 1][activeQuestion.value].type;
    store.CurrentGame.rounds[activeRound.value][activeQuestion.value].typeAlt = store.CurrentGame.rounds[activeRound.value - 1][activeQuestion.value].typeAlt;
    store.CurrentGame.rounds[activeRound.value][activeQuestion.value].timeAllotted = store.CurrentGame.rounds[activeRound.value - 1][activeQuestion.value].timeAllotted;
    store.CurrentGame.rounds[activeRound.value][activeQuestion.value].difficulty = store.CurrentGame.rounds[activeRound.value - 1][activeQuestion.value].difficulty;
  }
}

function removeQuestionSegment(segment, subSegment) {
  if (subSegment || subSegment === 0) {
    workingQuestion.value.segments[segment].data.splice(subSegment, 1);
    if (workingQuestion.value.segments[segment].dataAnswers) {
     workingQuestion.value.segments[segment].dataAnswers.splice(subSegment, 1);
      // const array = [ ...workingQuestion.value.segments[segment].dataAnswers ];
      // let newArray = [];
      // for (let a = 0; a < array.length; a += 1) {
      //   if (a !== subSegment) {
      //     if (array[a] > subSegment + 1) {
      //       newArray.push(array[a + 1]);
      //     } else {
      //       newArray.push(array[a]);
      //     }
      //   }
      // }
      // workingQuestion.value.answerData.reorder = newArray;
      // // workingQuestion.value.segments[segment].dataAnswers.splice(subSegment, 1);
    }
    if (workingQuestion.value.answerData.highlight.length) {
      const x = workingQuestion.value.answerData.highlight.indexOf(subSegment);
      if (x > -1) {
        workingQuestion.value.answerData.highlight.splice(x, 1);
      }
    }
    if (workingQuestion.value.answerData.reorder.length) {
      const array = workingQuestion.value.answerData.reorder.split(',');
      const origAns = array.indexOf((subSegment + 1).toString());
      array.splice(origAns, 1);
      let newString = [];
      for (let a = 0; a < array.length; a += 1) {
        if (array[a] > subSegment + 1) {
          const num = Number(array[a]) - 1;
          newString.push(num);
        } else {
          newString.push(array[a]);
        }
      }
      workingQuestion.value.answerData.reorder = newString.join(',');
    }
    return;
  }
  workingQuestion.value.segments.splice(segment, 1);
  deleteConfirmSegment.value = null;
}

function moveQuestionSegment(up, segment, subSegment) {
  const seg = workingQuestion.value.segments[segment];
  if (subSegment || subSegment === 0) {
    const subKey = up ? subSegment - 1 : subSegment + 1
    if (!!seg.data[subKey]) {
      if (workingQuestion.value.answerData.highlight.length) {
        const origAns = workingQuestion.value.answerData.highlight.indexOf(subSegment);
        const newAns = workingQuestion.value.answerData.highlight.indexOf(subKey);
        const needToSwitchAnswers = origAns !== newAns;
        if (needToSwitchAnswers) {
          if (origAns > -1) {
            workingQuestion.value.answerData.highlight.splice(origAns);
            workingQuestion.value.answerData.highlight.push(subKey);
          } else {
            workingQuestion.value.answerData.highlight.splice(newAns);
            workingQuestion.value.answerData.highlight.push(subSegment);
          }
        }
      }
      if (workingQuestion.value.answerData.reorder.length) {
        const array = workingQuestion.value.answerData.reorder.split(',');
        const origAns = array.indexOf((subSegment + 1).toString());
        const newAns = array.indexOf((subKey + 1).toString());
        array.splice(origAns, 1, subKey + 1);
        array.splice(newAns, 1, subSegment + 1);
        workingQuestion.value.answerData.reorder = array.join(',');
      }
      const temp = [seg.data[subKey], seg.dataAnswers[subKey]];
      seg.data[subKey] = seg.data[subSegment];
      seg.data[subSegment] = temp[0];
      seg.dataAnswers[subKey] = seg.dataAnswers[subSegment];
      seg.dataAnswers[subSegment] = temp[1];
    }
    return;
  }
  const newKey = up ? segment - 1 : segment + 1;
  if (!!workingQuestion.value.segments[newKey]) {
    const temp = JSON.parse(JSON.stringify(workingQuestion.value.segments[newKey]));
    workingQuestion.value.segments[newKey] = seg;
    workingQuestion.value.segments[segment] = temp;
  }
}

function markAsCorrect(key) {
  const q = workingQuestion.value.answerData.highlight;
  const i = q.indexOf(key);
  if (i === -1) {
    q.push(key)
  } else {
    q.splice(i, 1);
  }
}


const deleteConfirmSegment = ref(null);

function updateLink(segment) {
  // Update media link when filename is provided
  let fileName = workingQuestion.value.segments[segment].data;
  if (fileName.substring(0,4).toLowerCase() !== 'http') {
    const link = new URL(`../assets/${fileName}`, import.meta.url);
    workingQuestion.value.segments[segment].link = link.href;
  } else {
    workingQuestion.value.segments[segment].link = fileName;
  }
}

const crosswordBlank = () => ({
  vertical: false,
  position: {
    x: null,
    y: null,
  },
  word: '',
  hint: '',
});


// Word Hunt logic
const letterPools = [
  'AACIOT',
  'ABILTY',
  'ABJMOQ',
  'ACDEMP',
  'ACELRS',
  'ADENVZ',
  'AHMORS',
  'BIFORX',
  'DENOSW',
  'DKNOTU',
  'EEFHIY',
  'EGKLUY',
  'EGINTV',
  'EHINPS',
  'ELPSTU',
  'GILRUW'
];

const letterPoolsNew = [
  'AAEEGNSR',
  'ABBJOOLB',
  'ACHOPSRM',
  'AFFKPSTN',
  'AOOTTWNW',
  'CIMOTUSS',
  'DEILRXLV',
  'DELRVYRD',
  'DISTTYTP',
  'EEGHNWOR',
  'EEINSUWD',
  'EHRTVWDF',
  'EIOSSTNG',
  'ELRTTYSL',
  'HIMNUQLA',
  'HLNNRZRA',
  'AAEEGNSR123',
  'ABBJOOLB456',
  'ACHOPSRM789',
  'AFFKPSTN012',
  'AOOTTWNW345',
  'CIMOTUSS678',
  'DEILRXLV901',
  'DELRVYRD234',
  'DISTTYTP567',
  'EEGHNWOR890',
  'EEINSUWD123',
  'EHRTVWDF456',
  'EIOSSTNG789',
  'ELRTTYSL012',
  'HIMNUQLA345',
  'HLNNRZRA678'
];

function squareContent(x, y, index) {
  try {
    const sq = { ...workingQuestion.value.segments[index].data[x][y] };
    switch (sq.letter) {
      default: break;
      case ('Q'): {
        sq.letter = 'Qu';
        break;
      }
      case ('1'): {
        sq.letter = 'Ch';
        break;
      }
      case ('2'): {
        sq.letter = 'St';
        break;
      }
      case ('3'): {
        sq.letter = 'Er';
        break;
      }
      case ('4'): {
        sq.letter = 'Sa';
        break;
      }
      case ('5'): {
        sq.letter = 'Ss';
        break;
      }
      case ('6'): {
        sq.letter = 'Ne';
        break;
      }
      case ('7'): {
        sq.letter = 'On';
        break;
      }
      case ('8'): {
        sq.letter = 'En';
        break;
      }
      case ('9'): {
        sq.letter = 'Ra';
        break;
      }
      case ('0'): {
        sq.letter = 'Me';
        break;
      }
    }
    // if (!squareDisplay.value[x] || !squareDisplay.value[x][y]) {
    //   return '_';
    // }
    return sq;
  } catch {
    return {};
  }
}

function generateGridLetters(gridLength, gridHeight) {
  const x = gridLength;
  const y = gridHeight ? gridHeight : gridLength;
  const grid = {};
  let pools = [ ...letterPools ];
  console.log(pools);
  // Fill Horizontal (x)
  for (let a = 0; a < x; a += 1) {
    // Fill Vertical (y)
    grid[a] = {};
    for (let b = 0; b < y; b += 1) {
      // Refill the pools once all have been used
      if (!pools.length) {
        pools = [ ...letterPools ];
      }
      const poolMath = Math.floor((Math.random() * pools.length - 1) + 1)
      const pool = poolMath;
      const letterMath = Math.floor(Math.random() * pools[pool].length);
      const letter = letterMath;
      console.log(a, b, poolMath, letterMath, pools.length);
      grid[a][b] = {
        letter: pools[pool][letter],
        pool: pools[pool],
        roll: letter,
      };
      // Remove the pool so it doesn't get selected again
      pools.splice(pool, 1);
    }
  }
  return grid;
};

// Word Frag & Secret Word logic

const categoriesPool = themes;

const secretWordPool = [
  { secret: 'Johnny Cash',
    clues: ['Man in black', 'Country singer', 'Folsom Prison'],
  },
  {
    secret: 'Lasagna',
    clues: ['Baked','Noodles','Cheese'],
  },
  {
    secret: 'Circus',
    clues: ['Clown','Tent','Elephant'],
  },
  {
    secret: 'Church',
    clues: ['Steeple','Pew','Sunday'],
  },
  {
    secret: 'Canada',
    clues: ['Maple Syrup','Hockey','North'],
  },
  {
    secret: 'Leonardo da Vinci',
    clues: ['Renaissance polymath','Mona Lisa','The Last Supper'],
  },
  {
    secret: 'Eiffel Tower',
    clues: ['Paris','Landmark','Wrought iron'],
  },
  {
    secret: 'Harry Potter',
    clues: ['Wizardy School','Book','Movie'],
  },
  {
    secret: 'Chocolate',
    clues: ['Sweet treat','Willy Wonka','Cacao seeds'],
  },
  {
    secret: 'Mount Everest',
    clues: ['Tallest','Himalayas','Sherpas'],
  },
  {
    secret: 'The Beatles',
    clues: ['Liverpool','Rock music','Sgt Pepper'],
  },
  {
    secret: 'Salvador Dali',
    clues: ['Surrealism','Melting clocks','Moustache'],
  },
  {
    secret: 'Machu Picchu',
    clues: ['Incan citadel','World Heritage site','Peru'],
  },
  {
    secret: 'Vincent van Gogh',
    clues: ['Sunflowers','Starry Night','Ear'],
  },
  {
    secret: 'Cleopatra',
    clues: ['Ancient Egypt','Julius Caesar','Mark Antony'],
  },
  {
    secret: 'Albert Einstein',
    clues: ['E=mc²','Nobel Prize','Brilliant'],
  },
  {
    secret: 'The Sistine Chapel',
    clues: ['Vatican City','Michelangelo','Creation of Adam'],
  },
  {
    secret: 'Charles Darwin',
    clues: ['Galapagos Islands','Natural selection','HMS Beagle'],
  },
  {
    secret: 'Wolfgang Amadeus Mozart',
    clues: ['Child prodigy','The Magic Flute','Requiem'],
  },
  {
    secret: 'Ludwig van Beethoven',
    clues: ['Ode to Joy','Moonlight Sonata','Deaf'],
  },
  {
    secret: 'The Wizard of Oz',
    clues: ['Frank L Baum','Kansas','Witch'],
  },
  {
    secret: 'The Roman Colosseum',
    clues: ['Ampitheater','Ancient','Gladiators'],
  },
  {
    secret: 'The Great Barrier Reef',
    clues: ['World Heritage site','Australia','Ocean'],
  },
  {
    secret: 'The Renaissance',
    clues: ['Cultural rebirth','Michelangelo','Leonardo da Vinci'],
  },
  {
    secret: 'Emma Stone',
    clues: ['La La Land','Zombieland','Actress'],
  },
  {
    secret: 'Ryan Reynolds',
    clues: ['Deadpool','Van Wilder','Actor'],
  },
  {
    secret: 'Zendaya',
    clues: ['Dune','Mary Jane','Singer'],
  },
  {
    secret: 'Scarlett Johansson',
    clues: ['Ghost World','Black Widow','Lost in Translation'],
  },
  {
    secret: 'Tom Hiddleston',
    clues: ['Loki','Midnight in Paris','British'],
  },
  {
    secret: 'Ariana Grande',
    clues: ['Singer','Thank U Next','Ponytail'],
  },
  {
    secret: 'Brad Pitt',
    clues: ['Jesse James','Fight Club','Bullet Train'],
  },
  {
    secret: 'Edward Norton',
    clues: ['Fight Club','Hulk','Glass Onion'],
  },
  {
    secret: 'Lady Gaga',
    clues: ['A Star is Born','Bad Romance','Poker Face'],
  },
  {
    secret: 'Bradley Cooper',
    clues: ['Limitless','A Star is Born','Hangover'],
  },
  {
    secret: 'Rihanna',
    clues: ['Barbados','Diamonds','Fenty Beauty'],
  },
  {
    secret: 'Beyoncé',
    clues: ['Lemonade','Single Ladies','Destiny\'s Child'],
  },
  {
    secret: 'Dwayne Johnson (The Rock)',
    clues: ['Fast & Furious','Jumanji','Wrestling'],
  },
  {
    secret: 'Taylor Swift',
    clues: ['Shake it Off','1989','You Belong with Me'],
  },
  {
    secret: 'Cuban Missle Crisis',
    clues: ['JFK','Cold War confrontation','Brink of nuclear war'],
  },
  {
    secret: 'The Fall of the Berlin Wall',
    clues: ['Cold War', 'Reunification', 'November 9, 1989'],
  },
  {
    secret: 'The Industrial Revolution',
    clues: ['Technological advancements', 'New production methods', '18th century'],
  },
  {
    secret: 'The Boston Tea Party',
    clues: ['Colonial protest', 'Taxation without representation', '1773'],
  },
  {
    secret: 'The Black Death (Bubonic Plague)',
    clues: ['Medieval pandemic', 'Devastating mortality', '14th century'],
  },
  {
    secret: 'The Treaty of Versailles',
    clues: ['End of World War I', 'Reparations for Germany', '1919'],
  },
  {
    secret: 'Waterloo',
    clues: ['Location of Napoleon\'s final battle in 1815', 'Song by ABBA'],
  },
  {
    secret: 'The Great Depression',
    clues: ['Economic downturn', 'Stock market crash', '1929'],
  },
  {
    secret: 'The Russian Revolution',
    clues: ['End of Romanov dynasty', 'Bolsheviks', 'October 1917'],
  },
  {
    secret: 'The Mongol Empire',
    clues: ['Genghis Khan', 'Largest land empire', '13th-14th centuries'],
  },
  {
    secret: 'Thriller',
    clues: ['1980\'s', 'Zombies', 'Music video',],
  },
  {
    secret: 'Family Guy',
    clues: ['Brian', 'Peter', 'Lois', ],
  },
  {
    secret: 'South Park',
    clues: ['Kyle', 'Stan', 'Kenny'],
  },
  {
    secret: 'American Dad',
    clues: ['Roger the Alien', 'Stan', 'CIA'],
  },
];

const fragPool = [
  'ace',
  'able',
  'all',
  'anc',
  'and',
  'ane',
  'are',
  'ars',
  'art',
  'ats',
  'ban',
  'ben',
  'bin',
  'bor',
  'bon',
  'cam',
  'cap',
  'car',
  'cas',
  'cen',
  'ces',
  'com',
  'con',
  'cra',
  'cro',
  'cru',
  'cop',
  'cos',
  'cus',
  'cut',
  'dan',
  'dee',
  'dem',
  'den',
  'dep',
  'did',
  'dim',
  'din',
  'dip',
  'don',
  'dog',
  'eas',
  'eam',
  'ell',
  'end',
  'epp',
  'equ',
  'ere',
  'ers',
  'ert',
  'ets',
  'eve',
  'eva',
  'exe',
  'fab',
  'fal',
  'fam',
  'fan',
  'far',
  'fas',
  'fen',
  'fes',
  'fet',
  'ff',
  'ffy',
  'fin',
  'fis',
  'fit',
  'foe',
  'foo',
  'for',
  'fos',
  'fun',
  'fy',
  'gad',
  'gam',
  'gan',
  'gap',
  'gee',
  'gen',
  'gon',
  'goo',
  'gor',
  'got',
  'gum',
  'gus',
  'hab',
  'hac',
  'had',
  'hag',
  'hal',
  'ham',
  'han',
  'hap',
  'har',
  'hat',
  'hea',
  'hec',
  'hen',
  'her',
  'hey',
  'hid',
  'him',
  'hin',
  'hip',
  'his',
  'hon',
  'hop',
  'hos',
  'hot',
  'how',
  'hy',
  'ia',
  'ial',
  'ing',
  'ipp',
  'imp',
  'io',
  'ion',
  'iss',
  'its',
  'jar',
  'kid',
  'kin',
  'lac',
  'lam',
  'lan',
  'lar',
  'lap',
  'las',
  'lat',
  'law',
  'lay',
  'le',
  'lee',
  'ler',
  'lem',
  'len',
  'les',
  'let',
  'lib',
  'lic',
  'lid',
  'lie',
  'lif',
  'lim',
  'lin',
  'lio',
  'lis',
  'lit',
  'lle',
  'lly',
  'loa',
  'lob',
  'loc',
  'log',
  'lon',
  'loo',
  'lop',
  'lot',
  'los',
  'low',
  'lov',
  'lug',
  'lul',
  'lut',
  'mad',
  'maf',
  'mag',
  'mal',
  'man',
  'map',
  'mas',
  'mat',
  'may',
  'me',
  'mea',
  'med',
  'meg',
  'mel',
  'mem',
  'men',
  'mer',
  'met',
  'mi',
  'mid',
  'min',
  'mis',
  'mit',
  'mod',
  'mos',
  'mon',
  'mou',
  'nab',
  'nam',
  'nap',
  'nas',
  'nat',
  'nea',
  'nel',
  'ner',
  'nes',
  'net',
  'new',
  'nic',
  'nin',
  'nny',
  'nod',
  'nor',
  'nos',
  'not',
  'nov',
  'oan',
  'oin',
  'ook',
  'oon',
  'ool',
  'oom',
  'oon',
  'om',
  'omp',
  'ont',
  'ond',
  'ons',
  'our',
  'out',
  'pac',
  'pad',
  'pan',
  'pas',
  'pat',
  'pen',
  'per',
  'pes',
  'pet',
  'pin',
  'pit',
  'pis',
  'pos',
  'pp',
  'pun',
  'py',
  'qua',
  'qui',
  'quo',
  'rab',
  'rad',
  'rag',
  'ram',
  'ran',
  'rar',
  'ras',
  'rat',
  'ray',
  're',
  'rea',
  'red',
  'reg',
  'ret',
  'rep',
  'rew',
  'ri',
  'ric',
  'rid',
  'rig',
  'rit',
  'rip',
  'roa',
  'rob',
  'roc',
  'rom',
  'rod',
  'rog',
  'rol',
  'rop',
  'ros',
  'rot',
  'rou',
  'row',
  'rud',
  'rug',
  'rui',
  'rr',
  'sag',
  'sal',
  'sam',
  'san',
  'sap',
  'sas',
  'sat',
  'saw',
  'sea',
  'see',
  'seg',
  'sel',
  'sem',
  'sen',
  'sep',
  'set',
  'ser',
  'sim',
  'sin',
  'sir',
  'sn',
  'sni',
  'son',
  'soo',
  'sol',
  'som',
  'son',
  'sor',
  'sp',
  'ss',
  'ssy',
  'su',
  'sub',
  'sun',
  'sur',
  'sy',
  'tab',
  'tag',
  'tal',
  'tar',
  'tat',
  'tap',
  'tan',
  'ti',
  'tie',
  'tim',
  'tin',
  'tip',
  'tir',
  'te',
  'tea',
  'ted',
  'tee',
  'tel',
  'tem',
  'ter',
  'toa',
  'toe',
  'tom',
  'ton',
  'too',
  'tor',
  'top',
  'tot',
  'tou',
  'tow',
  'toy',
  'tu',
  'tra',
  'tre',
  'tri',
  'tro',
  'tt',
  'ty',
  'ua',
  'ub',
  'ui',
  'um',
  'un',
  'up',
  'va',
  'van',
  'var',
  'val',
  've',
  'ven',
  'vet',
  'ves',
  'vis',
  'vin',
  'vy',
  'wan',
  'wag',
  'wal',
  'war',
  'wat',
  'wel',
  'win',
  'won',
  'woo',
  'x',
  'ya',
  'ye',
  'yo',
  'you',
  'yu',
  'z',
  'za',
  'ze',
  'zo',
  'zy',
];

function visibleLetters(word, answerData) {
  let toReturn = '';
  const alwaysShow = [
    ' ',
    ',',
    '\'',
    '.',
    '!',
    '?',
    '(',
    ')',
    '-',
    '*',
    '+',
    '÷',
    '='
  ]
  for (let a = 0; a < word.length; a += 1) {
    if ((answerData && answerData.split(',')[a]) || alwaysShow.indexOf(word[a]) > -1) {
      toReturn += word[a];
    } else {
      toReturn += '*';
    }
  }
  return toReturn;
}

function generateFrags(count) {
  const frags = [];
  for (let a = 0; a < count; a += 1) {
    if (frags.length < fragPool.length) {
    let frag = Math.floor((Math.random() * fragPool.length - 1) + 1);
      while (frags.indexOf(frag) > -1) {
        frag = Math.floor((Math.random() * fragPool.length - 1) + 1);
      }
      frags.push(frag);
    }
  }
  return frags;
};

function generateCategories(count) {
  const cats = [];
  for (let a = 0; a < count; a += 1) {
    if (cats.length < themes.length) {
    let cat = Math.floor((Math.random() * categoriesPool.length - 1) + 1);
      while (cats.indexOf(cat) > -1) {
        cat = Math.floor((Math.random() * categoriesPool.length - 1) + 1);
      }
      cats.push(cat);
    }
  }
  return cats;
};

function generateSecretWords(count) {
  const secrets = [];
  for (let a = 0; a < count; a += 1) {
    if (secrets.length < secretWordPool.length) {
    let secret = Math.floor((Math.random() * secretWordPool.length - 1) + 1);
      while (secrets.indexOf(secret) > -1) {
        secret = Math.floor((Math.random() * secretWordPool.length - 1) + 1);
      }
      secrets.push(secret);
    }
  }
  return secrets;
};

const eras = [
  'N/A',
  'Modern/Current',
  'Ancient',
  'Medieval',
  'Renaissance',
  'Enlightenment',
  'Industrial Revolution',
  '1700s',
  '1800s',
  'Late 19th/Early 20th Century',
  '1900s',
  '1940s',
  '1950s',
  '1960s',
  '1970s',
  'Mid 20th Century',
  '1980s',
  '1990s',
  'Late 20th/Early 21st Century',
  '2000s',
  '2010s',
  '2020s',
  'Future',
];


watch(() => 'props.workingQuestion', () => {
      console.log('workingQuestion changed');
      emit('update', props.workingQuestion);
    },
    { deep: true }
);

const questionTypes = ref([
  '',
  'Animals & Nature',
  'Art',
  'Automotive',
  'Food & Drink',
  'History',
  'History (Local)',
  'Holidays',
  'Geography & Civics',
  'General',
  'Literature',
  'Mathematics',
  'Military',
  'Movies',
  'Music',
  'Myth & Folklore',
  'Pop Culture',
  'Puzzles & Logic',
  'Science & Tech',
  'Sports',
  'Technology',
  'Television',
  'Tiebreaker',
  'Word Games',
]);

const originalFile = ref(null);

onMounted(() => {
  originalFile.value = JSON.parse(JSON.stringify(props.workingQuestion));
});

const changesMade = computed(() => {
  return JSON.stringify(props.workingQuestion) !== JSON.stringify(originalFile.value);
});

watch (() => changesMade, (newVal) => {
  console.log('changes reported');
});

watch(() => workingQuestion, (newVal) => {
  console.log('workingQuestion changed');
  // originalFile.value = JSON.parse(JSON.stringify(newVal));
});

function copyQuestion() {
  const copiedQ = JSON.parse(JSON.stringify(workingQuestion.value));
  // store.CurrentGame.rounds[activeRound.value][activeQuestion.value].dateCreated = new Date();
  // store.CurrentGame.rounds[activeRound.value][activeQuestion.value].dateUpdated = new Date();
  copiedQ.dateCreated = new Date();
  copiedQ.dateUpdated = new Date();
  store.copyQuestion = copiedQ;
}

const showPasteOptions = ref(false);

async function pasteQuestion(processId, manager) {
  if (!manager) { // Active Game Editor
    const existingId = store.CurrentGame.rounds[activeRound.value][activeQuestion.value]._id;
    if (processId === 'replace') { // Replace existing question with copied question
      store.CurrentGame.rounds[activeRound.value][activeQuestion.value] = { ...store.copyQuestion };
    } else if (processId === 'overwrite') { // Replace question, but keep existing question ID (overwriting existing question)
      store.CurrentGame.rounds[activeRound.value][activeQuestion.value] = { ...store.copyQuestion };
      store.CurrentGame.rounds[activeRound.value][activeQuestion.value]._id = existingId;
    } else if (processId === 'newId') { // Replace question, create new ID (upon saving)
      store.CurrentGame.rounds[activeRound.value][activeQuestion.value] = { ...store.copyQuestion };
      delete store.CurrentGame.rounds[activeRound.value][activeQuestion.value]._id;
    }
  } else { // Manager
    const qIndex = store.questions.findIndex((q) => q._id === props.workingQuestion._id);
    const question = store.questions[qIndex];
    const existingId = question._id;
    // Replace question, but keep existing question ID (overwriting existing question)
     store.questions[qIndex] = store.copyQuestion;
     store.questions[qIndex]._id = existingId;
  }
  // store.copyQuestion = null;
  showPasteOptions.value = false;
}

function setFormatTheme(up, segmentKey, clueKey, themeType) {
  const existingTheme = workingQuestion.value.segments[segmentKey].data[clueKey].format[themeType].theme;
  const themes = store.themes;
  console.log(themes);
  let index = existingTheme && existingTheme.title ? themes.findIndex((theme) => theme.title === existingTheme.title) : 0;
  if (up) {
    index = (index - 1 + themes.length) % themes.length; // Wrap around to the last item if index goes below 0
  } else {
    index = (index + 1) % themes.length; // Wrap around to the first item if index exceeds the length
  }
  workingQuestion.value.segments[segmentKey].data[clueKey].format[themeType].theme = { ...themes[index] };
}

function segmentHasAnswers(segment) {
  let answers = 0;
  segment.data.forEach((seg) => {
    if (seg.highlightOnAnswer) {
      answers += 1;
    }
  });
  return answers > 0;
}

function setRandomTheme() {
  const themes = store.themes;
  const index = Math.floor(Math.random() * themes.length);
  workingQuestion.value.format = { ...themes[index] };
  console.log(workingQuestion.value.format);
}

</script>

<template>
  <div
      v-if="workingQuestion"
      class="manager__question-container">
    <div
        class="manager__segment-container">
      <div
          class="settings">
        ⏰
        <input
            id="timeInput"
            v-model="workingQuestion.timeAllotted"
            placeholder="time"
        />
        <input
            id="categoryInput"
            type="text"
            v-model="workingQuestion.category"
            placeholder="Category"
        />
        <input
            id="categoryInput"
            type="text"
            v-model="workingQuestion.categorySubText"
            placeholder="Category Sub Text"
        />
        <select
            id="typeSelection"
            v-model="workingQuestion.type"
            placeholder="Genre">
          <option
              v-for="option in questionTypes"
              :value="option">
            {{option}}
          </option>
        </select>
        <select
            id="typeSelection"
            v-model="workingQuestion.typeAlt"
            placeholder="Genre (Alternative)">
          <option
              v-for="option in questionTypes.filter((type) => type !== workingQuestion.type)"
              :value="option">
            {{option}}
          </option>
        </select>
        <!-- Question Color Scheme -->
        <div
            v-if="workingQuestion.format">
          <label
              for="themePicker">
            Theme Colors
          </label>
          <select
              id="themePicker"
              v-model="workingQuestion.format">
              <option
                v-for="option in FormatStyles"
                :value="option">
              {{option.label}}
            </option>
          </select>
          <button
              @click="setRandomTheme()"
              style="display: inline-block">
            🎲
          </button>
        </div>
        <!-- Difficulty rating, 1-3 -->
        <div
            class="difficulty">
          Difficulty
          <span
              v-for="rank in 3"
              :key="rank"
              @click="workingQuestion.difficulty = rank"
              :class="workingQuestion.difficulty >= rank ? 'rank' : ''">
            <AnFilledStar
                class="difficulty-star"
                v-if="workingQuestion.difficulty >= rank"
            />
            <AnOutlinedStar
                class="difficulty-star"
                v-else
            />
          </span>
        </div>
        <!-- Points Input -->
        <label
            for="pointsInput">
          Points
        </label>
        <input
            id="pointsInput"
            type="number"
            v-model="workingQuestion.points"
            placeholder="Points"
            style="font-size: 2.5em;max-width: 80px"
        />
        <!-- Eras Dropdown -->
        <select
            id="eraSelection"
            v-model="workingQuestion.timePeriod"
            placeholder="Era">
          <option
              v-for="option in eras"
              :value="option">
            {{option}}
          </option>
        </select>
      </div>
      <div
      v-for="(segment, index) in workingQuestion.segments"
      class="manager__segment">
      <move-and-delete
          @up="moveQuestionSegment(true, index)"
          @down="moveQuestionSegment(false, index)"
          @delete="removeQuestionSegment(index)"
          :content="'Segment'"
          :style="'display: inline-block'"
      />
      {{index + 1}})
        <select
            v-if="segment && segment.type"
            @change="changeSegment(segment.type, index)"
            v-model="segment.type"
            class="manager__segment__select">
          <option v-for="option in segmentTypes" :value="option.value">
            {{ option.text }}
          </option>
        </select>
        <div
            v-if="segment.type === 'text'">
          <ckeditor
              :editor="editor"
              class="manager__segment-input"
              :config="editorConfig"
              v-model="segment.data"
          />
          <button
              id="increaseMagnify"
              style="display: inline-block"
              @click="segment.magnify > 0 ? segment.magnify -= 0.25 : segment.magnify = 0">
              -
            </button>
            <button
            id="increaseMagnify"
            style="display: inline-block"
            @click="segment.magnify > 0 ? segment.magnify += 0.25 : segment.magnify = 0">
            +
          </button>
          <input
              v-model="segment.magnify"
              type="number"
              placeholder="Magnify"
          />
        </div>
        <div
            v-if="segment.type === 'audio' || segment.type === 'video' || segment.type === 'image'">
          <input
              v-model="segment.data"
              @input="updateLink(index)"
          />
          <input
              v-model="segment.showOnAnswer"
              type="checkbox"
              v-if="segment.type === 'image'"
          />
          <input
              v-model="segment.magnify"
              type="number"
          />
        </div>
        <div
            v-if="segment.type === 'wordLeap'">
            <div
                v-for="(wordLeap, wlKey) in segment.data">
              <move-and-delete
                  @up="moveQuestionSegment(true, index, wlKey)"
                  @down="moveQuestionSegment(false, index, wlKey)"
                  @delete="removeQuestionSegment(index, wlKey)"
                  :content="'Word'"
              />
              <input
                v-if="!props.hideAnswers"
                v-model="segment.data[wlKey]"
              />
              <span
                  v-else
                  class="mr-4 large-text"
                  v-text="visibleLetters(segment.data[wlKey], segment.dataAnswers[wlKey])"
              />
              <input
                  v-model="segment.dataAnswers[wlKey]"
                  placeholder="Shown Letters (comma seperated)"
              />
            </div>
            <button
                @click="segment.data.push('')">
                ➕
            </button>
            <input
                v-model="workingQuestion.answerData.hint.amount"
            /> Hint (0 - less hints, 1, or 2 - most hints)
            <br />
            Delay hint
            <input
                type="checkbox"
                v-model="workingQuestion.answerData.hint.delay"
            />
             Magnify
            <input
                type="number"
                v-model="segment.magnify"
            />
        </div>
        <div
            v-if="segment.type === 'blankFill'">
            <div
                v-for="(word, wordKey) in segment.data"
                class="input-row">
              <move-and-delete
                  @up="moveQuestionSegment(true, index, wordKey)"
                  @down="moveQuestionSegment(false, index, wordKey)"
                  @delete="removeQuestionSegment(index, wordKey)"
                  :content="'Word'"
              />
              <input
                  :id="`wordInput_${segment._id}`"
                  v-if="!props.hideAnswers"
                  v-model="segment.data[wordKey]"
                  placeholder="Word"
              />
              <span
                  v-else
                  class="large-text"
                  v-text="visibleLetters(segment.data[wordKey], segment.dataAnswers[wordKey])"
              />
              <input
                  :id="`visibleLettersInput_${segment._id}`"
                  v-model="segment.dataAnswers[wordKey]"
                  placeholder="Visible Letters (comma seperated)"
              />
            </div>
            Magnify
            <input
                type="number"
                v-model="segment.magnify"
            />
            <button
                @click="segment.data.push(''); segment.dataAnswers.push('')">
                ➕
            </button>
        </div>
        <div
            v-if="segment.type === 'bet'">
          Points:
          <input
              v-model="segment.data.amount"
          />
          Time:
          <input
              v-model="segment.data.time"
          />
        </div>
        <div
            v-if="segment.type === 'multipleChoice'">
            <div
                class="multiple-choice__container">
              <div
                v-for="(multiChoice, mcKey) in segment.data"
                class="multiple-choice">
                <move-and-delete
                    :content="'Option'"
                    :stacked="true"
                    @up="moveQuestionSegment(true, index, mcKey)"
                    @down="moveQuestionSegment(false, index, mcKey)"
                    @delete="removeQuestionSegment(index, mcKey)"
                    
                />
                <textarea
                    placeholder="Option"
                    v-model="segment.data[mcKey]"
                />
                <button
                    v-if="!props.hideAnswers"
                    @click="markAsCorrect(mcKey)"
                    :class="workingQuestion.answerData.highlight.indexOf(mcKey) > -1 ? 'highlight' : ''">
                  <span
                      v-text="workingQuestion.answerData.highlight.indexOf(mcKey) > -1 ? '✔️' : '❌'"
                  />
                </button>
                <input
                    v-if="segment.dataAnswers && !props.hideAnswers"
                    :placeholder="`Answer Text ${mcKey + 1}`"
                    v-model="segment.dataAnswers[mcKey]"
                    style="width: 150px"
                    />
              </div>
              <button
                  class="editor-button"
                  @click="segment.data.push('')">
                  ➕
              </button>
            </div>
            <button
                id="increaseMagnify"
                style="display: inline-block"
                @click="segment.magnify > 0 ? segment.magnify -= 0.25 : segment.magnify = 0">
                -
              </button>
              <button
              id="increaseMagnify"
              style="display: inline-block"
              @click="segment.magnify > 0 ? segment.magnify += 0.25 : segment.magnify = 0">
              +
            </button>
            <input
                v-model="segment.magnify"
                type="number"
                placeholder="Magnify"
            />
        </div>
        <div
            v-if="segment.type === 'clueAnswer'">
          <div
                class="clue-answer-edit__container">
            <div
                v-if="!segment.hideIndex || segment.forceAnswer">
              {{segment.hideIndex && segment.forceAnswer ? 'Answer Split' : 'Index Split'}}
              <input
                  v-if="segment.indexSplit"
                  v-model="segment.indexSplit[0]"
              />
              <input
                  v-if="segment.indexSplit"
                  v-model="segment.indexSplit[1]"
              />
            </div>
            <div>
              <div
                  v-for="(multiChoice, clueKey) in segment.data"
                  class="clue-answer-edit">
                <move-and-delete
                    :content="'Clue'"
                    @up="moveQuestionSegment(true, index, clueKey)"
                    @down="moveQuestionSegment(false, index, clueKey)"
                    @delete="removeQuestionSegment(index, clueKey)"
                    
                />
                <input
                    :id="`indexLabelInput-${index}-${clueKey}`"
                    v-if="!segment.hideIndex"
                    :placeholder="`#${clueKey + 1}`"
                    v-model="segment.data[clueKey].indexLabel"
                    style="width: 150px"
                />
                <textarea
                    :id="`clueInput-${index}-${clueKey}`"
                    placeholder="Clue"
                    v-model="segment.data[clueKey].clue"
                />
                <input
                    :id="`answerInput-${index}-${clueKey}`"
                    v-if="segment.dataAnswers && !props.hideAnswers && (!segment.hideIndex || segment.forceAnswer)"
                    :placeholder="`Answer Text ${clueKey + 1}`"
                    v-model="segment.data[clueKey].answer"
                    style="width: 150px"
                />
                <button
                    v-if="segment.dataAnswers && !props.hideAnswers"
                    :style="!segmentHasAnswers(segment) ? 'background-color: darkgrey; color: grey' : ''"
                    @click="segment.data[clueKey].highlightOnAnswer = !segment.data[clueKey].highlightOnAnswer">
                  <span
                      v-if="segmentHasAnswers(segment)"
                      v-text="segment.data[clueKey].highlightOnAnswer ? '✔️' : '❌'"
                  />
                  <span
                      v-else>
                    ✔️
                  </span>
                </button>
                <div
                    :id="`segmentFormat-${index}-${clueKey}`"
                    v-if="segment.data[clueKey].format"> <!-- legacy v-if to prevent old questions from erroring out-->
                    <div
                        :id="`IndexFormat-${index}-${clueKey}`"
                        v-if="!segment.hideIndex">
                        Index
                      <div
                          :id="`formatSelection-${index}-${clueKey}`"
                          :style="segment.data[clueKey].format && segment.data[clueKey].format.index && segment.data[clueKey].format.index.theme ? `background-color: ${segment.data[clueKey].format.index.theme.backround }; color: ${segment.data[clueKey].format.index.theme.color }` : ''">
                          <button
                            @click="setFormatTheme(false, index, clueKey, 'index')">
                          ↓
                        </button>
                        <span
                            v-text="segment.data[clueKey].format && segment.data[clueKey].format.index && segment.data[clueKey].format.index.theme && segment.data[clueKey].format.index.theme.title ? segment.data[clueKey].format.index.theme.title : 'Custom'"
                        />
                        <button
                            @click="setFormatTheme(true, index, clueKey, 'index')">
                          ↑
                        </button>
                      </div>
                    <!-- Index Background
                    <input
                        :id="`clueInput-background-${index}-${clueKey}`"
                        type="color"
                        v-model="segment.data[clueKey].format.index.background"
                    />
                    Text
                    <input
                        :id="`clueInput-text-${index}-${clueKey}`"
                        type="color"
                        v-model="segment.data[clueKey].format.index.color"
                    /> -->
                  </div>
                  <div
                      :id="`ClueFormat-${index}-${clueKey}`">
                    Clue
                    <div
                        :id="`clueFormatSelection-${index}-${clueKey}`">
                      <button
                          @click="setFormatTheme(false, index, clueKey, 'clue')">
                        ↓
                      </button>
                      <span
                          v-text="segment.data[clueKey].format && segment.data[clueKey].format.clue && segment.data[clueKey].format.clue.theme && segment.data[clueKey].format.clue.theme.title ? segment.data[clueKey].format.clue.theme.title : 'Custom'"
                      />
                      <button
                          @click="setFormatTheme(true, index, clueKey, 'clue')">
                        ↑
                      </button>
                    </div>
                    <!-- <input
                        :id="`clueInput-background${index}-${clueKey}`"
                        type="color"
                        v-model="segment.data[clueKey].format.clue.background"
                    />
                    Text
                    <input
                        :id="`clueInput-text-${index}-${clueKey}`"
                        type="color"
                        v-model="segment.data[clueKey].format.clue.color"
                    /> -->
                  </div>
                  <div
                      :id="`AnswerFormat-${index}-${clueKey}`"
                      v-if="!segment.hideIndex || segment.forceAnswer">
                      Answer
                      <div
                          :id="`answerFormatSelection-${index}-${clueKey}`">
                        <button
                            @click="setFormatTheme(false, index, clueKey, 'answer')">
                          ↓
                        </button>
                        <span
                            v-text="segment.data[clueKey].format && segment.data[clueKey].format.answer && segment.data[clueKey].format.answer.theme && segment.data[clueKey].format.answer.theme.title ? segment.data[clueKey].format.answer.theme.title : 'Custom'"
                        />
                        <button
                            @click="setFormatTheme(true, index, clueKey, 'answer')">
                          ↑
                        </button>
                      </div>
                    <!-- <input
                        :id="`clueInput-background-${index}-${clueKey}`"
                        type="color"
                        v-model="segment.data[clueKey].format.answer.background"
                    />
                    Text
                    <input
                        :id="`clueInput-text-${index}-${clueKey}`"
                        type="color"
                        v-model="segment.data[clueKey].format.answer.color"
                    /> -->
                    <!-- copy previous -->
                    <button
                        v-if="clueKey > 0"
                        @click="segment.data[clueKey].format = JSON.parse(JSON.stringify(segment.data[clueKey - 1].format))">
                      Copy Previous
                    </button>
                    <button
                        v-if="clueKey < segment.data.length - 1"
                        @click="segment.data[clueKey].format = JSON.parse(JSON.stringify(segment.data[clueKey + 1].format))">
                      Copy Below
                    </button>
                  </div>
                  Magnify Segment
                  <input
                      type="number"
                      v-model="segment.data[clueKey].format.magnify"
                </div>
              </div>
            </div>
            <button
                class="editor-button"
                @click="segment.data.push({clue: '', answer: '', indexLabel: '', highlightOnAnswer: false, format: { answer: {}, clue: {}, index: {}, magnify: 1 }})">
                ➕
            </button>
            <div>
              Top-Down Sorting
              <input
                  id="hideIndex"
                  type="checkbox"
                  v-model="segment.topDown"
              />
            </div>
            <div>
              Disable Index Column
              <input
                  id="hideIndex"
                  type="checkbox"
                  v-model="segment.hideIndex"
              />
            </div>
            <div
                v-if="segment.hideIndex">
              Show 'answer'
              <input
                  id="hideIndex"
                  type="checkbox"
                  v-model="segment.forceAnswer"
              />
            </div>
            <div>
              Flip Index
              <input
                  id="reverseIndex"
                  type="checkbox"
                  v-model="segment.reverseIndex"
              />
            </div>
            <div>
              Columns
              <button
                  id="clueColumns"
                  @click="segment.columns > 5 ? segment.columns = 1 : segment.columns += 1"
                  v-text="segment.columns"
              />
            </div>
          </div>
          <button
              id="increaseMagnify"
              style="display: inline-block"
              @click="!segment.magnify || segment.magnify <= 0 ? segment.magnify -= 0.25 : segment.magnify = 1">
              -
            </button>
            <button
            id="increaseMagnify"
            style="display: inline-block"
            @click="!segment.magnify || segment.magnify <= 0 ? segment.magnify += 0.25 : segment.magnify = 1">
            +
          </button>
          <input
              v-model="segment.magnify"
              type="number"
              placeholder="Magnify"
          />
        </div>
        <div
            v-if="segment.type === 'wordHunt'">
          Grid Size
          <input
              type="number"
              v-model="segment.gridCount"
              placeholder="Grid Size"
          />
          Minimum Word Length
          <input
              type="number"
              v-model="segment.minLength"
              placeholder="Min. Length"
          />
          <table
              id="wordHuntPreview"
              class="manager__preview">
            <tbody>
              <tr
                  v-for="x in segment.gridCount"
                  :key="x">
                <td
                    v-for="y in segment.gridCount"
                    :key="y">
                  <span
                    :id="`letter-${x - 1}-${y - 1}`"
                    v-if="squareContent(x - 1, y - 1, index).letter"
                    v-text="squareContent(x - 1, y - 1, index).letter"
                  />
                </td>
              </tr>
            </tbody>
          </table>
          <button
              id="regenWordHunt"
              @click="segment.data = generateGridLetters(segment.gridCount)">
            Refresh
          </button>
        </div>
        <div
            v-if="segment.type === 'anagramScramble'">
          Grid Height
          <input
              type="number"
              v-model="segment.gridLength"
              placeholder="Grid Length"
              @input="segment.data = generateGridLetters(segment.gridLength, segment.gridHeight)" 
          />
          <br />
          Grid Length
          <input
              type="number"
              v-model="segment.gridHeight"
              placeholder="Grid Height"
              @input="segment.data = generateGridLetters(segment.gridLength, segment.gridHeight)"
          />
          <br />
          Minimum Word Length
          <input
              type="number"
              v-model="segment.minLength"
              placeholder="Min. Length"
          />
          <table
              id="wordHuntPreview"
              class="manager__preview">
            <tbody>
              <tr
                  v-for="x in segment.gridLength"
                  :key="x">
                <td
                    v-for="y in segment.gridHeight"
                    :key="y">
                  <span
                    :id="`letter-${x - 1}-${y - 1}`"
                    v-if="squareContent(x - 1, y - 1, index).letter"
                    v-text="squareContent(x - 1, y - 1, index).letter"
                  />
                </td>
              </tr>
            </tbody>
          </table>
          <button
              id="regenWordGrid"
              @click="segment.data = generateGridLetters(segment.gridLength, segment.gridHeight)">
            Refresh
          </button>
        </div>
        <div
            v-if="segment.type === 'wordFrag'">
          # of Frags
          <input
              type="number"
              v-model="segment.fragCount"
              placeholder="Puzzle Size"
          />
          Spaced out
          <input
              type="checkbox"
              v-model="segment.delayEach"
          />
          <p
              v-for="(frag, x) in segment.data"
              class="manager__preview"
              :key="x"
              v-text="segment.pool[frag]"
          />
          <button
              id="regenWordHunt"
              @click="segment.data = generateFrags(segment.fragCount)">
            Refresh
          </button>
          Bonus Rules:
          <input
              type="number"
              v-model="segment.enableBonus"
              placeholder="number of bonuses"
              @input="segment.rules = wordFragRules(segment.enableBonus)"
          />
          <p
              v-for="(rule, k) in segment.rules"
              :key="k"
              v-html="rule"
              class="manager__preview"
          />
          Override:
          <input
              v-model="segment.override"
          />
        </div>
        <div
            v-if="segment.type === 'synonymScramble'">
          # of Words
          <input
              type="number"
              v-model="segment.wordCount"
              placeholder="Puzzle Size"
          />
          Spaced out
          <input
              type="checkbox"
              v-model="segment.delayEach"
          />
          <p
              v-for="(frag, x) in segment.data"
              class="manager__preview"
              :key="x"
              v-text="segment.pool && segment.pool[frag] ? segment.pool[frag] : null"
          />
          <button
              id="regenWordHunt"
              @click="segment.data = generateFrags(segment.fragCount)">
            Refresh
          </button>
          Bonus Rules:
          <input
              type="number"
              v-model="segment.enableBonus"
              placeholder="number of bonuses"
              @input="segment.rules = wordFragRules(segment.enableBonus)"
          />
          <p
              v-for="(rule, k) in segment.rules"
              :key="k"
              v-html="rule"
              class="manager__preview"
          />
          Override:
          <input
              v-model="segment.override"
          />
        </div>
        <div
            v-if="segment.type === 'categoryCrush'">
          # of Categories
          <input
              type="number"
              v-model="segment.categoryCount"
              placeholder="# of Categories"
          />
          Letter
          <input
              v-model="segment.randomLetter"
              placeholder="Letter"
          />
          Spaced out
          <input
              type="checkbox"
              v-model="segment.delayEach"
          />
          <p
              v-for="(frag, x) in segment.data"
              class="manager__preview"
              :key="x"
              v-text="segment.pool[frag]"
          />
          <button
              id="regenCategoryCrush"
              @click="segment.data = generateCategories(segment.categoryCount)">
            Refresh
          </button>
          Bonus Rules:
          <input
              type="number"
              v-model="segment.enableBonus"
              placeholder="number of bonuses"
              :max="categoryCrushBonuses.length"
              @input="segment.rules = categoryCrushRules(segment.enableBonus)"
          />
          <p
              v-for="(rule, k) in segment.rules"
              :key="k"
              v-html="rule"
              class="manager__preview"
          />
        </div>
        <div
            v-if="segment.type === 'wordCards'">
          <button
              @click="segment.data.push('')">
            Add Word
          </button>
          <div
              v-for="(wordCard, x) in segment.data"
              class="manager__preview"
              :key="x">
            <input
                v-model="segment.data[x]"
            />
            <button
                @click="segment.data.splice(x, 1)">
              Remove
            </button>
          </div>
          <button
              id="regenCategoryCrush"
              @click="segment.data = generateCategories(segment.categoryCount)">
            Refresh
          </button>
          Prompt:
          <input
              v-model="segment.prompt"
              type="text"
          />
        </div>
        <div
            v-if="segment.type === 'alphabetCrush'">
          Category
          <input
              type="number"
              v-model="segment.categoryCount"
              placeholder="# of Categories"
          />
          Letter
          <input
              v-model="segment.randomLetter"
              placeholder="Letter"
          />
          Spaced out
          <input
              type="checkbox"
              v-model="segment.delayEach"
          />
          <p
              v-for="(frag, x) in segment.data"
              class="manager__preview"
              :key="x"
              v-text="segment.pool[frag]"
          />
          Override
          <input
              v-model="segment.category"
          />
          <button
              id="regenCategoryCrush"
              @click="segment.data = generateCategories(segment.categoryCount)">
            Refresh
          </button>
          Bonus Rules:
          <input
              type="number"
              v-model="segment.enableBonus"
              placeholder="number of bonuses"
              :max="categoryCrushBonuses.length"
              @input="segment.rules = categoryCrushRules(segment.enableBonus)"
          />
          <p
              v-for="(rule, k) in segment.rules"
              :key="k"
              v-html="rule"
              class="manager__preview"
          />
        </div>
        <div
            v-if="segment.type === 'secretWord'">
          # of Secret Words
          <input
              type="number"
              v-model="segment.wordCount"
              placeholder="Puzzle Size"
          />
          Spaced out
          <input
              type="checkbox"
              v-model="segment.delayEach"
          />
          <p
              v-for="(secret, x) in segment.data"
              class="manager__preview"
              :key="x">
            <strong>
              {{segment.pool[secret].secret}}
            </strong>
            <p
                v-for="(clue, y) in segment.pool[secret].clues"
                v-text="clue"
            />
          </p>
          <button
              id="regenWordHunt"
              @click="segment.data = generateSecretWords(segment.wordCount)">
            Refresh
          </button>
        </div>
        <div
            v-if="segment.type === 'crossword'">
            <div
                v-for="(word, wordKey) in segment.data">
                <move-and-delete
                    @up="moveQuestionSegment(true, index, wordKey)"
                    @down="moveQuestionSegment(false, index, wordKey)"
                    @delete="removeQuestionSegment(index, wordKey)"
                    :content="'Segment'"
                />
              <input
                  placeholder="word"
                  v-if="!props.hideAnswers"
                  v-model="segment.data[wordKey].word"
              />
              <span
                  v-else
                  class="large-text"
                  v-text="visibleLetters(segment.data[wordKey].word)"
              />
              <textarea
                  placeholder="hint"
                  v-model="segment.data[wordKey].hint"
              />
              <br />
              <input
                  type="checkbox"
                  v-model="segment.data[wordKey].vertical"
              />
              <input
                  type="number"
                  v-model="segment.data[wordKey].position.x"
                  placeholder="position(across)"
              />
              <input
                  type="number"
                  v-model="segment.data[wordKey].position.y"
                  placeholder="position(down)"
              />
            </div>
            <button
                @click="segment.data.push(crosswordBlank())">
                ➕
            </button>
        </div>
        <div
            v-if="segment.type === 'fillInBlank'">
          <input
              v-model="segment.data"
          />
        </div>
      </div>
      <button
          id="addSegmentButton"
          @click="addQuestionSegment()"
          class="editor-button">
          Add Segment
      </button>
      <button
          v-if="!props.manager && activeRound !== 0"
          @click="addQuestionSegment(true)"
          style="margin: 15px">
        Copy Previous Round
      </button>
      <button
          @click="copyQuestion()"
          style="margin: 15px">
        Copy Question
      </button>
      <button
          v-if="store.copyQuestion"
          @click="showPasteOptions = !showPasteOptions"
          style="margin: 15px">
        Replace/Paste Question
      </button>
      <div
          v-if="showPasteOptions">
        <button
            v-if="!props.manager"
            @click="pasteQuestion('replace', props.manager)">
          Replace Question With Saved Version
        </button>
        <button
            @click="pasteQuestion('overwrite', props.manager)">
          Overwrite Existing Question
        </button>
        <button
            v-if="!props.manager"
            @click="pasteQuestion('newId', props.manager)">
          Create a New Question 
        </button>
        <button
            @click="showPasteOptions = false">
          Cancel
        </button>
      </div>
      <div
          v-if="!props.hideAnswers && workingQuestion.answerData"
          id="answerContainer">
        <input
            v-model="workingQuestion.answerData.text"
            placeholder="Answer"
        />
        <input
            v-model="workingQuestion.answerData.reorder"
            placeholder="Comma separated order"
        />
      </div>
      <div
          id="extraInfo">
        <textarea
            v-model="workingQuestion.extraInfo"
            placeholder="Extra Info"
        />
      </div>
    </div>
  </div>
</template>

<style
    lang="scss" scoped>
@import '@/assets/base.css';



/* #app {
  background: rgb(0,0,0);
  margin: 0 !important;
  padding: 0 !important;
  cursor: default;
}
.app-container {
  background: rgb(75,97,84);
  background: radial-gradient(circle, rgba(75,97,84,1) 0%, rgba(64,96,120,1) 100%);
  color: white;
  margin: 0;
  height: 100vh;
  width: 100vw;
  background: linear-gradient(350deg, rgba(255,255,255,1) 0%, rgba(73,179,247,1) 12%, rgba(217,213,77,1) 37%, rgba(80,88,193,1) 57%, rgba(159,227,210,1) 76%, rgba(225,250,255,1) 92%);
  background-size: 400% 400%;
  animation: swirl 30s ease-in-out infinite;
}


@keyframes swirl {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
} */


img {
  max-height: 66vh;
  margin: auto;
  border-radius: 10px;
  display: flex;
}
/* .option-image {
  /* width: 21vw !important;
  height: 21vh !important;
} */
video {
  max-width: 90vw;
  max-height: 85vh;
  border: black solid 1px;
  border-radius: 15px;
  margin: auto;
  display: flex;
}


.large-text {
  font-size: 1.5em;
}

.editor-button {
  font-size: 1.2em;
  background-color: #007BFF;
  color: white;
  padding: 8px 15px;
  border: none;
  border-radius: 5px;
  display: block;
  margin: 0 auto;
}

.answer-fade-enter-active {
  transition: all 0.7s ease-out;
}
.answer-fade-leave-active {
  transition: all 0.5s ease-out;
}
.answer-fade-enter-from {
  transform: translateX(-50vw);
  opacity: 0;
}
.answer-fade-leave-to {
  transform: translateX(50vw);
  opacity: 0;
}

.category-fade-enter-active {
  transition: all 0.7s ease-out;
}
.category-fade-leave-active {
  transition: all 0.5s ease-out;
}
.category-fade-enter-from {
  transform: translateX(-45vw);
}
.category-fade-leave-to {
  transform: translateX(-70vw);
}


.slide-fade-enter-active {
  transition: all 0.6s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.4s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateX(20px);
  opacity: 0;
}

.slide-right-enter-active, .slide-center-enter-active, .slide-left-enter-active {
  transition: all 0.6s ease-out;
}

.slide-right-leave-active, .slide-center-leave-active, .slide-left-leave-active {
  transition: all 0.6s ease-out;
}

.slide-right-enter-from {
  transform: translateX(-50px);
  opacity: 0;
}
.slide-right-leave-to {
  transform: translateX(50px);
  opacity: 0;
}
.fade-enter-active {
  transition: all 0.6s ease-out;
}

.fade-leave-active {
  transition: all 0.6s ease-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Trivia Night */

/* #question-container {
  position: relative;
} */

.small-button {
  font-size: 0.8em;
  margin-right: 1em;
}
/*
.trivia-container {
  height: 87vh;
  width: 100vw;
  /* position: absolute; */
  /* display: flex;
  top: 0;
  right: 0;
  /* background: inherit;
  font-size: 6.5em;
  text-align: center;
  align-items: center;
  /* top: 50%;
  text-align: center;
  height: 90vh;
  width: 100vw; */
  /* position: absolute;
  top: 0;
  right: 0;
  height: 90vh;
  width: 100vw; */
  /* font-size: 7vmin;
  padding-bottom: 10vh; */
  /* margin-top: auto;
  margin-bottom: auto;
}

.trivia-container::before {
  content: '';
  display: inline-block;
  height: 100%; 
  vertical-align: middle;
  /* margin-right: -0.25em; Adjusts for spacing */

  /* For visualization 
  background: #808080; width: 5px;
  */

.settings {
  background: rgba(0,0,0,0.5);
  padding: 10px;
  border-radius: 5px;
}
.difficulty {
  margin-left: 15px;
  display: inline-block;
  &-star {
    font-size: 2.5em;
    &:hover {
      color: lighten(blue, 35%);
    }
  }
}

.question {
  display: inline-block;
  vertical-align: middle;
  width: 100vw;
  /* position: relative;
  top: 0;
  transform: translateY(10%);
  width: 100vw; */
  /* height: calc(87vh - 1px); */
}

#ticker {
  background-color: gold;
}

.clue-answer-edit {
  display: flex;
  background-color: lighten(darkslategrey, 50%);
  color: darkslategrey;
  border: 2px solid slategrey;
  box-shadow: 2px 2px 5px 2px rgba(0,0,0,0.25);
  margin: 3px auto;
  padding: 10px 20px;
  transition: all ease 0.5s;
  align-items: center;
  justify-content: center;
  &__container {
    display: inline;
    flex-direction: column;
    /* display: grid;
    grid-template-columns: repeat(1, 1fr);
    grid-template-rows: 1fr;
    grid-column-gap: 0px;
    grid-row-gap: 0px; */
  }
}
.multiple-choice {
  display: flex;
  background-color: lighten(darkslategrey, 50%);
  color: darkslategrey;
  border: 2px solid slategrey;
  box-shadow: 2px 2px 5px 2px rgba(0,0,0,0.25);
  margin: 3px auto;
  padding: 10px 45px;
  transition: all ease 0.5s;
  &__container {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: 1fr;
    grid-column-gap: 0px;
    grid-row-gap: 0px;
  }
}
.trivia-multiple-choice {
  background-color: darken(azure, 30%);
  color: darkslategrey;
  border: solid gold 2px;
  padding: 15px;
  margin: 1.5vh 1.5vw;
  border-radius: 5px;
  font-size: 0.8em;
  &__correct {
    /* background-color: gold; */
    color: darkslategrey;
    transition: all ease 1.1s;
  }
  &__incorrect {
    background-color: darkslategrey;
    color: lighten(darkslategrey, 50%);
    transition: all ease 1.1s;
  }
  &__0 {
    background-color: #fcd303;
    color: darken(#fcd303, 31%);
  }
  &__1 {
    background-color: #03fc77;
    color: darken(#03fc77, 31%);
  }
  &__2 {
    background-color: #03e3fc;
    color: darken(#03e3fc, 31%);
  }
  &__3 {
    background-color: lighten(#fc0341, 20%);
    color: darken(#fc0341, 41%);
  }
  &__4 {
    background-color: #1ab7e7;
    color: darken(#21cbff, 41%);
  }
  &__5 {
    background-color: #fc69ff;
    color: darken(#fc69ff, 51%);
  }
}

/* .trivia-container {
  height: 100vh;
} */



.answer-container {
  background-color: black;
  height: 12vh;
  bottom: 0vh;
  display: grid;
  justify-content: center;
  align-content: center;
}


.bet-text {
  font-size: 0.5em;
  margin: 10px;
  padding: 20px;
}
strong {
  font-weight: 800;
  color: gold;
}

/* .info {
  color: lighten(gold, 15%);
  text-shadow: 2px 2px 2px rgba(0, 0, 0, 0.3);
} */





.timer {
  flex: 0.5;
  text-align: right;
  margin-right: 1.5vw;
  font-size: 4.75em;
  height: inherit;
}

.input-row {
  display: flex;
  /* align-items: center; */
  justify-content: space-between;
}

.countdown {
  display: flex;
  height: 100vh;
  width: 100vw;
  bottom: 0;
  left: 0;
}


.memory-square {
    width: 20vw;
    height: 15vh;
    margin: 2vmax auto;
    border-radius: 5px;
    border: 5px solid white;
  }
  .blue {
    background-color: blue;
  }
  .blue-inactive {
    background-color: rgba(blue, 0.3);
  }
  .red {
    background-color: red;
  }
  .red-inactive {
    background-color: rgba(red, 0.3);
  }
  .yellow {
    background-color: yellow;
  }
  .yellow-inactive {
    background-color: rgba(yellow, 0.3);
  }
  .white {
    background-color: white;
  }
  .white-inactive {
    background-color: rgba(white, 0.3);
  }


  .word-leap {
    margin-bottom: 1vmax;
    display: flex;
    border-radius: 10px;
    padding: 0px 3vmax;
    &__container {
      background:darken( cornflowerblue, 35%);
      margin: auto;
      padding: none;
      width: 45vw;
    }
    &:first-of-type {
      color: black;
      background:darken( cornflowerblue, 35%);
    }
    &:last-of-type {
      color: black;
      background:darken(cornflowerblue, 35%);
    }
    &__letter {
      border: 2px solid gold;
      border-radius: 10px;
      margin: 3px;
      color: black;
      background: gold;
      width: 65px;
      height: 60px;
      padding: none;
      transition: all 2.2s ease-out;
      font-size: 2.5rem;
    }
    &__blank {
      background: cornflowerblue;
      color: cornflowerblue;
    }
  }

  .scoreboard {
    position: fixed;
    bottom: 0vh;
    width: 100vw;
    background: silver;
    transition: all 1s ease;
    margin: auto;
  }
  .teams {
    &__name {
      font-size: 3em;
      width: 60vw;
    }
    &__score {
      margin-left: 1vw;
      margin-right: 2vw;
      font-size:4em;
      line-height: 0;
      border: 2px solid azure;
      background: rgb(75,97,84);
      background: radial-gradient(circle, rgba(75,97,84,1) 0%, rgba(119,120,64,1) 100%);
      color: white;
      padding: 0px 25px;
      &__high {
        color: gold;
        font-weight: 800;
      }
    }
    &__controls {
      display: inline-block;
      width: 15vw;
      &__toAdd {
        width: 5vw;
      }
    }
  }

  .ai {
    &__wordSet {
      display: block;
      margin: 1em;
    }
    &__word {
      display: inline-block; 
      margin: 0.5em;
      background: cornflowerblue;
      padding: 0.5em;
      color: khaki;
      border-radius: 5px;
      cursor: pointer;
    }
  }
  .highlight {
      background-color: rgba(gold, 0.7);
      color: darkslategrey;
  }
  .manager {
    position: absolute;
    text-align: center;
    overflow: scroll;
    z-index: 99;
    top: 0;
    width: 100vw;
    &__header {
      background-color: darkslategrey;
      border-radius: 10px;
      font-size: 2em;
      margin-left: auto;
      margin-right: auto;
      padding: 0.5em 0px;
    }
    &__rounds {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      grid-template-rows: 1fr;
      grid-column-gap: 0px;
      grid-row-gap: 0px;
      text-align: center;
      margin-top: 25px;
      &__sidebar {
        background: rgba(darkslategrey, 0.8);
        font-size: 2em;
        padding: 1vmax;
        margin-right: 10px;
        margin-left: auto;
        border-radius: 5px;
        width: 350px;
      }
      &__questions {
        margin: auto;
        margin-left: 10px;
      }
      &__question {
        padding: 8px;
        cursor: pointer;
      }
    }
    &__question-container {
      width: 66vw;
      padding: 10px;
      background: darkslategrey;
      display: flex;
      justify-content: center;
    }
    &__segment-container {
      padding: 5px;
      background: khaki;
      color: black;
      width: 62vw;
    }
    &__segment {
      background: darken(khaki, 20%);
      margin: 10px;
      padding: 10px;
      border-radius: 5px;
      border: 2px solid darkslategrey;
      &__select {
        margin-right: 10vw;
      }
    }
    &__segment-input {
      min-width: 50vw;
      min-height: 10vh;
    }
    &__question-number {
      background-color: azure;
      color: black;
      padding: 5px;
      border-radius: 5px;
      margin: 10px;
    }
    &__preview {
      margin: 20px auto;
      background-color: rgba(black, 0.6);
      padding: 10px 20px;
      border-radius: 10px;
      font-size: 1.3em;
    }
  }
  .ck-content {
    color: black;
  }
  #categoryInput {
    width: 20vw;
    font-size: 1.4em;
  }
  #timeInput {
    width: 65px;
    font-size: 1.4em;
  }
  #typeSelection {
    width: 15vw;
    font-size: 1.4em;
    margin: 10px;
  }
  .grid-layout {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  }
/* End Trivia Night */
</style>
